import { useQuery, useMutation, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  fetchPublicSurveyByUUID,
  fetchPublicTeamHealthMetricsByUUID,
  fetchActionPlanFocusCategory,
  fetchActionPlanTemplate,
  createActionPlan,
  fetchActionPlanAggregated,
  FocusCategory,
  ActionPlanTemplate,
  CreateActionPlanRequest,
  ActionPlan,
  ActionPlanAggregated,
} from '../../api/dashboardPublicApi';
import { TeamHealthMetrics, PublicSurvey } from '../../api/queries';
import { ApiError } from '../../utils/api-error.utils';
import { QueryKey } from '../../enums/QueryKey';
import { mapI18nLanguageToApiLanguage } from './language.mapper';

export const useGetPublicSurveyByUUIDQuery = (
  uuid: string,
  options?: Omit<UseQueryOptions<PublicSurvey, ApiError>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<PublicSurvey, ApiError>({
    queryKey: [QueryKey.PUBLIC_SURVEY, uuid],
    queryFn: () => fetchPublicSurveyByUUID(uuid),
    ...options,
  });
};

export const useGetPublicTeamHealthMetricsByUUIDQuery = (
  uuid: string,
  options?: Omit<
    UseQueryOptions<TeamHealthMetrics[], ApiError>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<TeamHealthMetrics[], ApiError>({
    queryKey: [QueryKey.PUBLIC_TEAM_HEALTH_METRICS, uuid],
    queryFn: () => fetchPublicTeamHealthMetricsByUUID(uuid),
    ...options,
  });
};

// Action Plan Queries
export const useGetActionPlanFocusCategoryQuery = (
  uuid: string,
  options?: Omit<UseQueryOptions<FocusCategory, ApiError>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<FocusCategory, ApiError>({
    queryKey: [QueryKey.ACTION_PLAN_FOCUS, uuid],
    queryFn: () => fetchActionPlanFocusCategory(uuid),
    ...options,
  });
};

export const useGetActionPlanTemplateQuery = (
  uuid: string,
  categoryId: number,
  options?: Omit<UseQueryOptions<ActionPlanTemplate, ApiError>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<ActionPlanTemplate, ApiError>({
    queryKey: [QueryKey.ACTION_PLAN_TEMPLATE, uuid, categoryId],
    queryFn: () => fetchActionPlanTemplate(uuid, categoryId),
    ...options,
  });
};

export const useGetActionPlanAggregatedQuery = (
  uuid: string,
  options?: Omit<UseQueryOptions<ActionPlanAggregated, ApiError>, 'queryKey' | 'queryFn'>,
) => {
  const { i18n } = useTranslation();
  const apiLanguage = mapI18nLanguageToApiLanguage(i18n.language);

  return useQuery<ActionPlanAggregated, ApiError>({
    queryKey: [QueryKey.ACTION_PLAN_AGGREGATED, uuid, apiLanguage],
    queryFn: () => fetchActionPlanAggregated(uuid, apiLanguage),
    ...options,
  });
};

export const useCreateActionPlanMutation = (
  uuid: string,
  options?: Omit<
    UseMutationOptions<ActionPlan, ApiError, CreateActionPlanRequest>,
    'mutationFn'
  >,
) => {
  return useMutation<ActionPlan, ApiError, CreateActionPlanRequest>({
    mutationFn: (data) => createActionPlan(uuid, data),
    ...options,
  });
};
